/**
 * usePresets hook — load/save/delete transform presets.
 * Replaces window.usePresets.
 */
import { useState, useCallback, useEffect } from 'react';
import * as dataService from '../services/dataService';
import type { Transform } from '../stores/types';

export type PresetMap = Record<string, Transform | Record<string, Transform>>;

export function usePresets() {
  const [presets, setPresets] = useState<PresetMap>({});

  // Load presets on mount
  useEffect(() => {
    dataService
      .loadPresets()
      .then((p) => setPresets(p as PresetMap))
      .catch(console.error);
  }, []);

  const savePreset = useCallback(
    async (name: string, data: Transform | Record<string, Transform>) => {
      setPresets((prev) => ({ ...prev, [name]: data }));
      const ok = await dataService.savePreset(name, data);
      if (!ok) alert('Ошибка сохранения на сервере');
    },
    [],
  );

  const deletePreset = useCallback(async (name: string) => {
    setPresets((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
    await dataService.deletePreset(name);
  }, []);

  const getPreset = useCallback(
    (name: string) => {
      return presets[name] || null;
    },
    [presets],
  );

  const isSingleTransformPreset = (preset: unknown): preset is Transform => {
    return !!preset && typeof preset === 'object' && ('x' in preset || 'scale' in preset);
  };

  return { presets, savePreset, deletePreset, getPreset, isSingleTransformPreset };
}
